// Find file: the offer shown beside a selection that names a file, and the
// picker of the files it may name. FindFile.svelte renders both; the views'
// keymaps call `fromSelection` for `gF`.
import { detectPath, type FileRef } from '../logic/findFile';
import { toast } from './toast.svelte';

/** Where the offer sits: viewport coordinates of the selection's start (`left`, `top`) and of its last line's bottom. */
export interface OfferAt {
  left: number;
  top: number;
  bottom: number;
}

/** Elements whose selected text is their own (typed text, the picker itself). */
const OWN_TEXT = 'input,textarea,select,[contenteditable]:not([contenteditable="false"]),dialog,.find-file-offer';

/** The file reference the page's selection names inside `root`, and where it ends; null when it names none. */
export function selectedRef(root: HTMLElement | null): { ref: FileRef; at: OfferAt } | null {
  const sel = typeof window === 'undefined' ? null : window.getSelection();
  if (!root || !sel || sel.isCollapsed || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  const node = range.commonAncestorContainer;
  const el = node.nodeType === Node.ELEMENT_NODE ? (node as Element) : node.parentElement;
  if (!el || !root.contains(el) || el.closest(OWN_TEXT)) return null;
  const ref = detectPath(sel.toString());
  if (!ref) return null;
  const rects = range.getClientRects();
  const first = rects.length ? rects[0] : range.getBoundingClientRect();
  const last = rects.length ? rects[rects.length - 1] : first;
  return { ref, at: { left: first.left, top: first.top, bottom: last.bottom } };
}

class FindFile {
  /** The offer beside the selection, or null. */
  offer: { ref: FileRef; at: OfferAt } | null = $state.raw(null);
  /** The reference the picker is open for, or null when it is closed. */
  picking: FileRef | null = $state.raw(null);
  /** The PR page's content, where selections are read. */
  root: HTMLElement | null = null;

  /** Open the picker for the offer shown. */
  pick(ref: FileRef) {
    this.offer = null;
    this.picking = ref;
  }

  /** `gF`: open the picker for the selection, or say what it needs. */
  fromSelection() {
    const hit = selectedRef(this.root);
    if (hit) this.pick(hit.ref);
    else toast.show({ text: 'Select a file path or name on the page, then press gF to find it.' });
  }

  close() {
    this.picking = null;
  }
}

export const findFile = new FindFile();
